import { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, CheckCircle, CircleAlert } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCreateStockIn } from "@/hooks/useStockTransactions";
import { useLocation } from "wouter";
import type { Item } from "@/types/item";
import { useInvoicePrefix } from "@/hooks/useInvoicePrefix";
import { SearchStockModal } from "@/components/modal-stock";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const stockInSchema = z.object({
  header: z.object({
    voucherType: z.literal("GI"),
    voucherDate: z.string().min(1, "Date is required"),
    codeNumber: z.string().min(1, "Voucher number is required"),
    reference: z.string().optional(),
    remarks: z.string().optional(),
  }),
  details: z
    .array(
      z.object({
        itemId: z.string().min(1, "Please select an item"),
        itemCode: z.string().optional(),
        description: z.string().optional(),
        quantity: z.number().min(0.01, "Quantity must be greater than 0"),
        costPrice: z.number().min(0, "Cost price must be positive"),
        amount: z.number().optional(),
      })
    )
    .min(1, "At least one item is required"),
});

type StockInFormValues = z.infer<typeof stockInSchema>;

export const StockIn = () => {
  const { format: formatDecimal, step } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const createStockIn = useCreateStockIn();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const { codeNumberOptions } = useInvoicePrefix("GI");

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<StockInFormValues>({
    resolver: zodResolver(stockInSchema),
    defaultValues: {
      header: {
        voucherType: "GI",
        voucherDate: format(new Date(), "yyyy-MM-dd"),
        codeNumber: "",
        reference: "",
        remarks: "",
      },
      details: [],
    },
  });

  // Auto-select default prefix number
  useEffect(() => {
    if (codeNumberOptions) {
      setValue(
        "header.codeNumber",
        codeNumberOptions.find((option) => option.isDefault === true)
          ?.codeNumber || ""
      );
    }
  }, [codeNumberOptions, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "details",
  });

  const details = watch("details");

  // Calculate total amount
  const totalAmount = details.reduce((sum, item) => {
    const amount = (item.quantity || 0) * (item.costPrice || 0);
    return sum + amount;
  }, 0);

  // Calculate total quantity
  const totalQuantity = details.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Update amount when quantity or cost price changes
  const handleCalculation = (index: number) => {
    const item = details[index];
    if (item) {
      const amount = (item.quantity || 0) * (item.costPrice || 0);
      setValue(`details.${index}.amount`, Number(formatDecimal(amount)));
    }
  };

  const handleAddItem = (item: Item) => {
    // Check if item already exists
    const exists = details.some((d) => d.itemId === item.id);
    if (exists) {
      toast({
        title: "Item Already Added",
        description: "This item is already in the list",
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    append({
      itemId: item.id,
      itemCode: item.itemCode,
      description: item.description,
      quantity: 1,
      costPrice: item.weightedAverage || 0,
      amount: item.weightedAverage || 0,
    });
  };

  const onSubmit = async (data: StockInFormValues) => {
    try {
      const prefixId = codeNumberOptions?.find(
        (option) => option.codeNumber === getValues("header.codeNumber")
      );

      await createStockIn.mutateAsync(
        {
          reference: data.header.reference,
          entryDate: data.header.voucherDate,
          details: data.header.remarks,
          prefixId: prefixId?.id,
          items: data.details.map((item) => ({
            stockCode: item.itemId,
            quantity: item.quantity,
            costPrice: item.costPrice,
          })),
        },
        {
          onSuccess: (response) => {
            toast({
              title: "Stock In Created",
              description: `Voucher ${response.data.voucherNumber} created successfully`,
              icon: (
                <CheckCircle
                  size={30}
                  className="animate-spin-once text-green-400"
                />
              ),
              duration: 5000,
            });
            navigate("/stock-transactions");
          },
          onError: (error: any) => {
            toast({
              title: "Failed to Create Stock In",
              description: error.response?.data?.message || error.message,
              variant: "destructive",
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 4000,
            });
          },
        }
      );
    } catch (error) {
      console.error("Error creating stock in:", error);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Stock In</h1>
        <Button variant="outline" onClick={() => navigate("/stock-transactions")}>
          Back to List
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Header Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Voucher Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="voucherDate">
                    Date <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    type="date"
                    {...register("header.voucherDate")}
                    className="h-10"
                  />
                  {errors.header?.voucherDate && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.header.voucherDate.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="codeNumber">
                    Voucher Number <span className="text-red-600">*</span>
                  </Label>
                  <Controller
                    name="header.codeNumber"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue
                            placeholder={
                              codeNumberOptions?.find(
                                (option) => option.isDefault === true
                              )?.codeNumber ||
                              "No available prefix number added"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-48">
                          {codeNumberOptions?.map((option) => (
                            <SelectItem
                              key={option.id}
                              value={option.codeNumber}
                            >
                              {option.codeNumber}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.header?.codeNumber && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.header.codeNumber.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="reference">Reference</Label>
                  <Input
                    {...register("header.reference")}
                    placeholder="e.g., PO-12345"
                    className="h-10"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="remarks">Remarks</Label>
                  <Input
                    {...register("header.remarks")}
                    placeholder="Optional remarks"
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Line Items <span className="text-red-600">*</span>
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSearchModalOpen(true)}
                  className="shadow"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>

              <SearchStockModal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
                onSelectItem={handleAddItem}
                mode="in"
              />

              {errors.details?.root && (
                <p className="text-red-600 text-sm">{errors.details.root.message}</p>
              )}

              {/* Line Items Table */}
              {fields.length > 0 ? (
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-800">
                    <TableRow>
                      <TableHead>Item Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-32">Quantity</TableHead>
                      <TableHead className="w-32">Cost Price (RM)</TableHead>
                      <TableHead className="w-32">Amount (RM)</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell className="font-medium">
                          {details[index]?.itemCode}
                        </TableCell>
                        <TableCell>{details[index]?.description}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={step}
                            {...register(`details.${index}.quantity`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) => {
                              register(`details.${index}.quantity`).onChange(e);
                              setTimeout(() => handleCalculation(index), 0);
                            }}
                            className="h-8"
                          />
                          {errors.details?.[index]?.quantity && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.details[index]?.quantity?.message}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={step}
                            {...register(`details.${index}.costPrice`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) => {
                              register(`details.${index}.costPrice`).onChange(e);
                              setTimeout(() => handleCalculation(index), 0);
                            }}
                            className="h-8"
                          />
                          {errors.details?.[index]?.costPrice && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.details[index]?.costPrice?.message}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {formatDecimal((details[index]?.amount || 0))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="h-8 w-8 p-0 text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground border rounded-md">
                  No items added. Search and select items to add them to the stock in
                  transaction.
                </div>
              )}

              {/* Totals */}
              {fields.length > 0 && (
                <div className="flex justify-end">
                  <div className="w-96 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Total Quantity:</span>
                      <span>{formatDecimal(totalQuantity)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total Amount:</span>
                      <span>RM {formatDecimal(totalAmount)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="submit"
                className="w-36 h-10  text-base"
                disabled={createStockIn.isPending}
              >
                {createStockIn.isPending ? "Creating..." : "Create Stock In"}
              </Button>
              
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
